import React from "react";
import ReactDOM from "react-dom/client";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { getAccessToken, isTokenExpired, parseJwtPayload } from "./lib/auth.js";
import LoginPage from "./pages/login/LoginPage.js";
import AppLayout from "./components/layout/AppLayout.js";
import "./index.css";

const DebtCollectionPage = React.lazy(
  () => import("./pages/debt-collection/DebtCollectionPage.js")
);
const InvoicesPage = React.lazy(() => import("./pages/dashboard/InvoicesPage.js"));
const ReportPage = React.lazy(() => import("./pages/reports/ReportPage.js"));
const TransactionsPage = React.lazy(() => import("./pages/transactions/TransactionsPage.js"));
const DoWeryfikacjiPage = React.lazy(() => import("./pages/costs/DoWeryfikacjiPage.js"));
const ClassificationRulesPage = React.lazy(() => import("./pages/classification-rules/ClassificationRulesPage.js"));
const KsefImportPage = React.lazy(() => import("./pages/ksef/KsefImportPage.js"));
const FinanceExportPage = React.lazy(() => import("./features/finance-export/FinanceExportPage.js"));
const EmailTemplatesPage = React.lazy(() => import("./pages/email-templates/EmailTemplatesPage.js"));
const IntegrationsPage = React.lazy(() => import("./pages/integrations/IntegrationsPage.js"));
const UsersPage = React.lazy(() => import("./pages/users/UsersPage.js"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      staleTime: 30_000,
    },
  },
});

/** Verifies the user is authenticated (token exists and not expired).
 *  Redirects to /login if not. */
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const token = getAccessToken();
  if (!token || isTokenExpired(token)) {
    return <Navigate to="/login" replace />;
  }
  return <>{children}</>;
}

/** Verifies authentication AND that the user's role is in allowedRoles.
 *  collection_operator accounts are hard-redirected to /debt-collection for any
 *  route they are not permitted to access (client-side UX guard only — server-side
 *  enforcement is the authoritative security gate). */
function RoleProtectedRoute({
  children,
  allowedRoles,
}: {
  children: React.ReactNode;
  allowedRoles?: string[];
}) {
  const token = getAccessToken();
  if (!token || isTokenExpired(token)) {
    return <Navigate to="/login" replace />;
  }

  if (allowedRoles) {
    const user = parseJwtPayload(token);
    if (user && !allowedRoles.includes(user.role)) {
      return <Navigate to="/debt-collection" replace />;
    }
  }

  return <>{children}</>;
}

/** Routes accessible only by financial_operator and admin (not collection_operator) */
const FINANCIAL_ROLES = ["financial_operator", "admin"];
const COLLECTION_READ_ROLES = ["financial_operator", "admin", "collection_operator"];
/** Feature 018: dedicated client-invoices tab — collection_operator only.
 *  financial_operator/admin already see all invoices via /invoices, so they don't need this route. */
const CLIENT_INVOICES_ROLES = ["collection_operator"];

export default function App() {
  const token = getAccessToken();
  const user = token && !isTokenExpired(token) ? parseJwtPayload(token) : null;
  const isCollectionOperator = user?.role === "collection_operator";

  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <React.Suspense fallback={<div className="loading-row">Ładowanie…</div>}>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route
              path="/debt-collection"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <DebtCollectionPage readOnly={isCollectionOperator} />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/invoices"
              element={
                <RoleProtectedRoute allowedRoles={FINANCIAL_ROLES}>
                  <AppLayout>
                    <InvoicesPage />
                  </AppLayout>
                </RoleProtectedRoute>
              }
            />
            <Route
              path="/client-invoices"
              element={
                <RoleProtectedRoute allowedRoles={CLIENT_INVOICES_ROLES}>
                  <AppLayout>
                    <InvoicesPage forcedInvoiceType="sales" readOnly title="Faktury klienckie" />
                  </AppLayout>
                </RoleProtectedRoute>
              }
            />
            <Route path="/costs" element={<Navigate to="/transactions" replace />} />
            <Route
              path="/reports"
              element={
                <RoleProtectedRoute allowedRoles={FINANCIAL_ROLES}>
                  <AppLayout>
                    <ReportPage />
                  </AppLayout>
                </RoleProtectedRoute>
              }
            />
            <Route
              path="/transactions"
              element={
                <RoleProtectedRoute allowedRoles={COLLECTION_READ_ROLES}>
                  <AppLayout>
                    <TransactionsPage
                      {...(isCollectionOperator ? { collectionScope: "client_incoming" as const } : {})}
                    />
                  </AppLayout>
                </RoleProtectedRoute>
              }
            />
            <Route
              path="/do-weryfikacji"
              element={
                <RoleProtectedRoute allowedRoles={FINANCIAL_ROLES}>
                  <AppLayout>
                    <DoWeryfikacjiPage />
                  </AppLayout>
                </RoleProtectedRoute>
              }
            />
            <Route
              path="/classification-rules"
              element={
                <RoleProtectedRoute allowedRoles={FINANCIAL_ROLES}>
                  <AppLayout>
                    <ClassificationRulesPage />
                  </AppLayout>
                </RoleProtectedRoute>
              }
            />
            <Route
              path="/ksef-import"
              element={
                <RoleProtectedRoute allowedRoles={FINANCIAL_ROLES}>
                  <AppLayout>
                    <KsefImportPage />
                  </AppLayout>
                </RoleProtectedRoute>
              }
            />
            <Route path="/non-invoice-incoming" element={<Navigate to="/transactions" replace />} />
            <Route path="/invoice-payments" element={<Navigate to="/invoices" replace />} />
            <Route
              path="/finance-export"
              element={
                <RoleProtectedRoute allowedRoles={FINANCIAL_ROLES}>
                  <AppLayout>
                    <FinanceExportPage />
                  </AppLayout>
                </RoleProtectedRoute>
              }
            />
            <Route
              path="/email-templates"
              element={
                <RoleProtectedRoute allowedRoles={FINANCIAL_ROLES}>
                  <AppLayout>
                    <EmailTemplatesPage />
                  </AppLayout>
                </RoleProtectedRoute>
              }
            />
            <Route
              path="/integrations"
              element={
                <RoleProtectedRoute allowedRoles={FINANCIAL_ROLES}>
                  <AppLayout>
                    <IntegrationsPage />
                  </AppLayout>
                </RoleProtectedRoute>
              }
            />
            <Route
              path="/users"
              element={
                <RoleProtectedRoute allowedRoles={FINANCIAL_ROLES}>
                  <AppLayout>
                    <UsersPage />
                  </AppLayout>
                </RoleProtectedRoute>
              }
            />
            <Route path="/" element={<Navigate to="/debt-collection" replace />} />
          </Routes>
        </React.Suspense>
      </BrowserRouter>
    </QueryClientProvider>
  );
}

const rootEl = document.getElementById("root");
if (rootEl) {
  ReactDOM.createRoot(rootEl).render(
    <React.StrictMode>
      <App />
    </React.StrictMode>
  );
}
